type VersionHistoryEntryBase = {
  version: string;
  commits: string[];
};

type LegacyVersionHistoryEntry = VersionHistoryEntryBase & {
  pushedOn: string;
  previewPushedOn?: never;
  productionPushedOn?: never;
};

type EnvironmentVersionHistoryEntry = VersionHistoryEntryBase & {
  pushedOn?: never;
  previewPushedOn: string;
  productionPushedOn?: string;
};

export type VersionHistoryEntry = LegacyVersionHistoryEntry | EnvironmentVersionHistoryEntry;

export const versionHistory: VersionHistoryEntry[] = [
  {
    version: "0.8.9",
    previewPushedOn: "2026-08-31",
    productionPushedOn: "2026-09-01",
    commits: [
      "Reintroduce Engine 1 as an active QRS apparatus matching Engine 2 - welcome back Engine 1 🎉",
      "Start all Engine 1 equipment at zero and add its requirements to compliance totals",
      "Standardize apparatus order as Engine 1, Engine 2, Engine 3, Rescue, Squad 3",
      "Remove redundant (PPE) suffixes from PPE Kit item titles"
    ]
  },
  {
    version: "0.8.8",
    previewPushedOn: "2026-08-31",
    productionPushedOn: "2026-09-01",
    commits: [
      "Temporarily hide Truck from apparatus and truck checks",
      "Replace legacy PPE suit lines with Tyvek Suit (with boot cover and hood)"
    ]
  },
  {
    version: "0.8.7",
    previewPushedOn: "2026-08-25",
    productionPushedOn: "2026-08-25",
    commits: [
      "Simplify item expirations to month and year",
      "Standardize expiration entry and display as MM/YYYY",
      "Preserve month-end expiration behavior across inventory workflows",
      "Filter the Stock workspace by status"
    ]
  },
  {
    version: "0.8.6",
    previewPushedOn: "2026-08-24",
    productionPushedOn: "2026-08-24",
    commits: [
      "Simplify Sterile Thermal Blanket labels",
      "Identify Alcohol Prep Pads as a glucose item",
      "Hide daily truck check entry point"
    ]
  },
  {
    version: "0.8.5",
    previewPushedOn: "2026-08-24",
    productionPushedOn: "2026-08-24",
    commits: [
      "Correct Lower Extremity Splint requirements to two per apparatus",
      "Update glucose-kit and pelvic sheet item labels",
      "Group OB Kit items without category headings",
      "Standardize OB Kit Blanket notes",
      "Rename the apparatus edit reason to Used on incident"
    ]
  },
  {
    version: "0.8.4",
    previewPushedOn: "2026-07-23",
    productionPushedOn: "2026-08-24",
    commits: [
      "Improve reliability on slow and interrupted connections",
      "Add connection health feedback and resilient in-view refreshes",
      "Cancel stale requests and safely retry cacheable reads",
      "Reduce bandwidth with compact responses and server-side searches",
      "Paginate and debounce changelog history",
      "Protect truck check updates from stale writes",
      "Make Stock saves resumable with conflict detection and reconciliation"
    ]
  },
  {
    version: "0.8.3",
    pushedOn: "2026-07-23",
    commits: [
      "Add footer-linked version history",
      "Fix truck check removals and changelog reverts",
      "Add in-view refresh controls",
      "Make monthly truck check primary",
      "Refine persistent refresh control",
      "Polish responsive application controls",
      "Move desktop refresh control to persistent menu bar",
      "Show push dates in version history"
    ]
  },
  {
    version: "0.8.2",
    pushedOn: "2026-05-28",
    commits: [
      "Refine daily grouped check display",
      "Polish daily grouped check labels",
      "Hide daily assessment section label",
      "Improve mobile date entry",
      "Stabilize date entry controls",
      "Apply manual date entry across app",
      "Reinforce stock date mobile sizing",
      "Tighten segmented date input spacing",
      "Match stock date input styling",
      "Highlight footer version badge",
      "Refine footer version badge"
    ]
  },
  {
    version: "0.8.1",
    pushedOn: "2026-05-27",
    commits: ["Simplify daily truck checks"]
  },
  {
    version: "0.8.0",
    pushedOn: "2026-05-17",
    commits: [
      "Initial commit",
      "Implement QRS inventory v1 app scaffold and workflows",
      "Rename dashboard to Status and fix apparatus compliance rollups",
      "Fix stock API status payload for stock room screen",
      "Remove header subtitle text",
      "feat: implement v2 workbook-driven reset and stock-lot movement",
      "refactor: simplify UX by removing admin lock and inline name entry",
      "fix: improve stock editing and simplify compliance labels",
      "Refine mobile UI shell and stock move flow",
      "Center stock movement dialog",
      "Remove status surface and update header logo sprite",
      "Adjust header eyebrow wording",
      "Refine compliance clarity and modal editing UX",
      "Refine compliance status hierarchy and center stock edit modal",
      "Harden stock/bin handling and add stock-only order report",
      "Separate QRS compliance from stock PAR planning",
      "Add guided truck check workflow with history and CSV export",
      "Refine nav labels and add planning CSV export",
      "Simplify ordering to stock PAR gap report",
      "Fix D1 transaction fallback for truck check start",
      "Refine mobile layout and nav stability across core screens",
      "Fix non-QRS seeding, truck check required tasks, and ordering parity",
      "Replace popup dialogs with styled move and confirm modals",
      "Align seed model to dashboard workbook and split ordering scope",
      "Refactor compliance view to table layout with apparatus filter",
      "Tighten compliance table headers and rename why to reason",
      "Simplify compliance tables and remove status column",
      "Standardize apparatus ordering across views and dropdowns",
      "Normalize bag topology and make backorder note-driven",
      "Show missing required bag items and normalize kit naming",
      "Update apparatus bag topology and pet kit naming",
      "Refine apparatus bag exclusions for rescue and truck",
      "Lock apparatus bag layouts to prevent source kit drift",
      "Persist entered name per-browser and finalize stock dedupe updates",
      "Support expiration-grouped inventory rows across bags and stock",
      "Add in-bag kit navigator and normalize medication category",
      "Refine apparatus drill-in and simplify bag navigation",
      "Rename person prompts to Your Name and remove audit-note inputs",
      "Add notes placeholder guidance for backorder tagging",
      "Add apparatus-wide kit and item search on apparatus detail",
      "Tag apparatus search results with kit labels",
      "Lock apparatus edits and require source tracking",
      "Add stock-linked apparatus source workflow",
      "Add bulk stock workspace with CSV tools",
      "Refine stock table and notes hint text",
      "Tune stock table bin and quantity column widths",
      "Widen stock page layout and remove row reset",
      "Add UTF-8 BOM to stock CSV export",
      "Override PPE requirements by apparatus",
      "Harden apparatus and stock edit workflows",
      "Fix sticky leading zero in quantity inputs",
      "feat(stock): support allowed-item lot adds and bin editing",
      "fix(stock): hide zero lots when item still has stock",
      "fix(stock): improve split and move quantity defaults",
      "feat(stock): suggest bins and allow add-to-allow-list from modal",
      "feat(stock): refine add modal search mode and bin selection",
      "feat(stock): tighten bin picker and custom-bin entry flow",
      "fix(stock): separate split modal and restore custom bin toggle",
      "fix(stock): align split mode with custom bin exposure",
      "fix(stock): keep new-bin field visible with guarded edit",
      "fix(stock): keep enter-new-bin selection from reverting",
      "fix(stock): default bins from existing lots when not adding new item",
      "fix(stock): align split bin defaults with add flow",
      "fix(stock): default split bin to current lot bin",
      "fix(stock): isolate split modal state from add search resets",
      "fix(stock): gate add qty default until item or add-new mode",
      "fix(stock): remove split guidance notice artifact",
      "Improve apparatus validation, ordering expiration sources, and explicit delete actions",
      "Refine stock delete confirmation and split ordering by action",
      "Remove ordering action helper note",
      "Require apparatus source and stock lot with explicit submit errors",
      "Add comprehensive changelog with single-change and point-in-time revert",
      "Fix changelog revert audit insert and separate change number column",
      "Block no-op and zero-to-zero apparatus saves",
      "Fix point-in-time revert audit FK handling",
      "Harden mutations with endpoint tests and DB integrity guards",
      "Add full-system CSV backup export/import on log page",
      "Allow changelog revert on runtimes without SQL BEGIN",
      "Normalize CSV export encoding and add coverage",
      "Delete zeroed apparatus split lines when sibling lots exist",
      "Rename apparatus add-lot flow to split semantics",
      "Implement true apparatus lot split flow and API",
      "Refine apparatus split flow and remove split qty cap",
      "Collapse duplicate zero stock lots",
      "Apply manual seed overrides for updated apparatus model",
      "Allow apparatus split using same expiration lot",
      "Add rigid suction catheter requirements and stock seed",
      "Persist selected kit when switching apparatus",
      "Refine kit mapping and compliance seed logic",
      "Preserve history on reseed and lock apparatus kit snapshot",
      "Add preview/testing warning banner to app header",
      "Remove reseed workflow and retire compliance items",
      "Pin preview warning banner",
      "Split Narcan PPE from QRS items",
      "Place Narcan items in QRS jump kit",
      "Remove Child BVM from Engine 3 QRS jump kit",
      "Refine apparatus item edit actions",
      "Restore quick apparatus item editing",
      "Add other apparatus item source",
      "Update QRS compliance item requirements",
      "Clean up hidden zero stock rows",
      "Add alert-only compliance requirements",
      "Clean up zero apparatus item placeholders",
      "Allow other as audit source type",
      "Require triage tags on all QRS apparatus",
      "Move Other below Stock in source dropdown",
      "Restore apparatus split action",
      "Fix apparatus split workflow",
      "Keep apparatus split available for stock source",
      "Disable stock split source when unavailable",
      "Disable unavailable stock source in item modal",
      "Allow apparatus splits without expiration",
      "Finalize triage tags Other kit compliance",
      "Add Narcan Band Aids QRS requirement",
      "Allow backup import in Pages runtime",
      "Batch system backup imports",
      "Remove Engine 2 Airway rigid suction artifact",
      "Remove Squad 3 PPE blanket biohazard artifacts",
      "Link bag header to apparatus",
      "Remove placeholder Extra bags",
      "Align monthly truck checks with apparatus inventory",
      "Simplify truck check missing label",
      "Add truck check verification prompts",
      "Add truck check quit flow",
      "Simplify truck check workflow wording",
      "Add related line item visual grouping",
      "Update truck check missing tag after corrections",
      "Warn on no-expiration apparatus splits",
      "Disable truck check source until item changes",
      "Refine split warning copy",
      "Warn when apparatus split will merge",
      "Fix truck check split task tracking",
      "Style truck check statuses",
      "Allow deleting abandoned truck checks",
      "Allow resuming abandoned truck checks",
      "Fix truck check status tags for split items",
      "Group split bag items for status checks",
      "Fix ordering report stock and compliance gaps",
      "Show stock PAR quantities",
      "Organize QRS jump kit by pouch",
      "Organize airway bag by sub-kit",
      "Alphabetize bag item groups",
      "Add daily QRS truck check workflow",
      "Refine truck check section spacing",
      "Clarify daily visual confirmation note",
      "Add daily Airway truck check tasks",
      "Add daily Suction truck check tasks",
      "Add daily AED truck check tasks",
      "Add remaining daily kit checks",
      "Remove related entries count chips",
      "Make stock PAR editable and add shift footer",
      "Soften shift footer styling",
      "Add D-Shift footer logo",
      "Exclude zero PAR items from ordering",
      "Clean up stock ordering data migrations",
      "Refine daily truck check visual tasks",
      "Fix glove expiration artifacts and rigid kit typo",
      "Remove apparatus-only items from stock planning",
      "Lock stock editing behind PIN",
      "Require removal reasons for apparatus item edits",
      "Require apparatus edit reasons",
      "Remove duplicate apparatus removal reason",
      "Separate apparatus and truck check remove reasons",
      "Hide apparatus reason during remove confirmation",
      "Hide apparatus name during remove confirmation",
      "Add modal close buttons",
      "Refine modal close button styling",
      "Make completed truck checks read-only",
      "Show daily check problem notes",
      "Highlight daily check problem reports",
      "Show truck check open confirmation",
      "Preserve daily visual task status",
      "Fix truck check modal and history UX",
      "Show app version in footer"
    ]
  }
];
